import { Mail, MessageCircle, Phone } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePersonCard } from '@/api/profiles';
import type { Person } from '@/api/types';
import { clientRoleLabel, studioRoleLabel } from '@/lib/access';
import { messengerHref, phoneHref } from '@/lib/contacts';

import { Avatar, Dialog, Loading } from './ui';

function Card({ id, onClose }: { id: string; onClose: () => void }) {
  const q = usePersonCard(id);
  const studioViewer = useLocation().pathname.startsWith('/studio');
  const card = q.data;
  if (!card) return q.isError ? <p className="p-5 text-sm text-muted">Профиль этого человека вам недоступен.</p> : <Loading />;
  const phone = phoneHref(card.phone);
  const chat = messengerHref(card.messenger);
  const where = card.side === 'studio' ? 'Студия Mark-42' : (card.clientName ?? 'Компания клиента');
  const role = card.studioRole ? studioRoleLabel[card.studioRole] : card.clientRole ? clientRoleLabel[card.clientRole] : '';
  const row = 'flex items-center gap-2.5 text-sm';
  const link = 'underline underline-offset-2';
  return (
    <div className="space-y-5 p-5">
      <div className="flex items-center gap-3">
        <Avatar person={card} />
        <div className="min-w-0">
          <div className="text-sm font-semibold">{card.name}</div>
          <div className="text-xs text-muted">{[card.role, role.toLowerCase()].filter(Boolean).join(' · ')}</div>
          <div className="text-xs text-faint">{where}</div>
        </div>
      </div>
      <ul className="space-y-2">
        {card.phone && (
          <li className={row}>
            <Phone className="size-4 text-muted" aria-hidden />
            {phone ? (
              <a className={link} href={phone}>
                {card.phone}
              </a>
            ) : (
              card.phone
            )}
          </li>
        )}
        {card.messenger && (
          <li className={row}>
            <MessageCircle className="size-4 text-muted" aria-hidden />
            {chat ? (
              <a className={link} href={chat} target="_blank" rel="noreferrer noopener">
                {card.messenger}
              </a>
            ) : (
              card.messenger
            )}
          </li>
        )}
        {card.email && (
          <li className={row}>
            <Mail className="size-4 text-muted" aria-hidden />
            <a className={link} href={`mailto:${card.email}`}>
              {card.email}
            </a>
          </li>
        )}
        {!card.phone && !card.messenger && <li className="text-xs text-faint">Телефон и мессенджер не указаны.</li>}
      </ul>
      {card.projects.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-xs text-muted">{card.side === 'studio' ? 'Проекты' : 'Проекты компании'}</h3>
          <ul className="divide-y divide-line border-y border-line text-sm">
            {card.projects.map((p) => (
              <li key={p.id}>
                <Link className="flex items-center justify-between gap-3 py-2 hover:text-ink" to={studioViewer ? `/studio/projects/${p.id}` : `/p/${p.id}`} onClick={onClose}>
                  <span className="truncate">{p.title}</span>
                  {p.lead && <span className="shrink-0 text-xs text-muted">ведёт</span>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** A name or an avatar that opens the person's card. The BFF decides who may be opened. */
export function PersonLink({ person, children, className }: { person: Pick<Person, 'id' | 'name'>; children?: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={className ?? 'rounded text-left hover:underline hover:underline-offset-2'}
        onClick={() => setOpen(true)}
        aria-label={children ? person.name : undefined}
      >
        {children ?? person.name}
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title={person.name}>
        <Card id={person.id} onClose={() => setOpen(false)} />
      </Dialog>
    </>
  );
}
